// Watch it build, in the canvas: the build state of a chat's App (published from the thread for the canvas), the
// building view until a build can be shown, the quiet "Updating…" mark and Select, which points the chat at a part of
// the App. Presentation only: it reads the thread's own messages and changes no execution or approval.
import { useEffect, useRef, useState, useSyncExternalStore, type FC } from 'react';
import { useNamespaceCopy } from '@/i18n/ns';
import buildWatchCopy from '@/i18n/ns/build-watch';
import { Button } from '@/components/ui/button';
import { useOptionalWorkspace } from '@/workspace/context';
import { CanvasHeaderLeadTools, CanvasHeaderTools } from '../../canvas-footer';
import { WorkspaceIcon } from '../../icons';
import {
  readAppSelection,
  resolvedThread,
  type AppBuildState,
  type PreviewView,
} from './live-build-model';
import './live-build.css';

const states = new Map<string, AppBuildState>();
const listeners = new Set<() => void>();
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => void listeners.delete(listener);
};
/** Published by the thread (live-build-observer.tsx) for its canvas. */
export function publishAppBuild(threadId: string, state: AppBuildState) {
  const previous = states.get(threadId);
  if (previous && JSON.stringify(previous) === JSON.stringify(state)) return;
  states.delete(threadId);
  states.set(threadId, state);
  while (states.size > 50) states.delete(states.keys().next().value!);
  listeners.forEach((listener) => listener());
}
// Answers this page has seen working, in order, and the order when it first showed each saved editor that Change with
// Chat opened: such a canvas follows the builds of the answers that started after it (live-build-model.ts `recent`).
let answerOrder = 0;
const answersSeen = new Map<string, number>();
const editorsShown = new Map<string, number>();
const bounded = (map: Map<string, number>) => {
  while (map.size > 500) map.delete(map.keys().next().value!);
};
export function noteAnswerWorking(messageId: string) {
  if (answersSeen.has(messageId)) return;
  answersSeen.set(messageId, ++answerOrder);
  bounded(answersSeen);
}
export const answerSeen = (messageId: string) => answersSeen.get(messageId);
function editorShownSince(key: string) {
  if (!editorsShown.has(key)) editorsShown.set(key, answerOrder);
  bounded(editorsShown);
  return editorsShown.get(key) ?? answerOrder;
}
/** Change with Chat opened this saved editor now (the server gives a saved version the same editor every time): only
 * answers that start after this opening replace it, whatever an earlier opening let through. */
export function noteEditorOpened(key: string) {
  editorsShown.delete(key);
  editorsShown.set(key, answerOrder);
  bounded(editorsShown);
  listeners.forEach((listener) => listener());
}
/** The answer order a shown saved editor follows builds from: set when first shown, or when Change with Chat opens it
 * again; ordinary renders and a stopped answer keep it. */
export function useEditorShownSince(key: string | null) {
  return useSyncExternalStore(
    subscribe,
    () => (key ? editorShownSince(key) : null),
    () => null,
  );
}
/** The thread's App build as last published, outside React (the chat transport's tool hooks). */
export const readAppBuild = (threadId: string) => states.get(threadId) ?? null;
export function useAppBuild(threadId: string | null | undefined) {
  return useSyncExternalStore(
    subscribe,
    () => (threadId ? (states.get(threadId) ?? null) : null),
    () => null,
  );
}

/** Where the person was in a thread's App (scroll, route): a canvas that opens it again starts there. */
const views = new Map<string, PreviewView>();
export const appView = {
  read: (threadId: string) => views.get(threadId) ?? null,
  keep(threadId: string, view: PreviewView) {
    views.delete(threadId);
    views.set(threadId, view);
    while (views.size > 50) views.delete(views.keys().next().value!);
  },
};

/** The thread this App canvas belongs to, once the chat the link names is open (resolvedThread). */
export function useResolvedThread(threadId: string): string | null {
  const ws = useOptionalWorkspace();
  return ws ? resolvedThread(threadId, ws.chatStatus, location.hash) : threadId || null;
}

/** Before the first build can be shown: a calm sketch taking shape, the App's name and what the chat is doing. */
export const AppBuilding: FC<{ build: AppBuildState | null }> = ({ build }) => {
  const copy = useNamespaceCopy(buildWatchCopy);
  const building = Boolean(build?.building);
  return (
    <div className="ap-building" data-building={building || undefined}>
      <div className="ap-building-sketch" aria-hidden="true">
        <span className="ap-building-top">
          <span data-part="mark" />
          <span data-part="name" />
          <span data-part="action" />
        </span>
        <span data-part="heading" />
        <span data-part="line" />
        <span className="ap-building-tiles">
          <span />
          <span />
          <span />
        </span>
      </div>
      <div className="ap-building-caption" role="status">
        {/* Waiting with no name yet, the hint already says "Your App". */}
        {(building || build?.title) && (
          <h2 className="ap-building-title">{build?.title || copy.yourApp}</h2>
        )}
        <p className={`ap-building-step${building ? ' pc-m-glint' : ''}`}>
          {building ? (build?.step ?? copy.cardBuilding) : copy.buildingHint}
        </p>
        {building && <span className="ap-building-rail pc-m-glide" aria-hidden="true" />}
      </div>
    </div>
  );
};

/**
 * Select in the App canvas header, beside a quiet "Updating…" while the chat changes the App. When Select is on, the
 * shown frame outlines what the pointer is over; a click there becomes the composer's selection chip and the next
 * message carries it. The frame's report is untrusted page data, kept only while Select is on.
 */
export function useAppSelect(send: (message: unknown) => void) {
  const ws = useOptionalWorkspace();
  const [selecting, setSelecting] = useState(false);
  const latest = useRef({ ws, selecting, send });
  latest.current = { ws, selecting, send };
  const chosen = Boolean(ws?.turnContext.appSelection);
  useEffect(() => {
    // The inspector's own hue (--pc-insp), in the current theme, drawn inside the App.
    const color = getComputedStyle(document.querySelector('.ws-canvas') ?? document.body)
      .getPropertyValue('--pc-insp')
      .trim();
    send({ type: 'poneva.inspect', on: selecting, ...(color ? { color } : {}) });
  }, [selecting, send]);
  // A sent or removed selection takes its outline with it.
  useEffect(() => {
    if (!chosen) send({ type: 'poneva.inspect', clear: true });
  }, [chosen, send]);
  return {
    selecting,
    setSelecting,
    receive(data: { on?: unknown; selected?: unknown }) {
      const { ws: current, selecting: on } = latest.current;
      if (data.on === false) setSelecting(false);
      if (!on || data.selected === undefined || !current) return;
      const selection = readAppSelection(data.selected);
      if (!selection) return;
      current.setTurnContext({ ...current.turnContext, appSelection: selection });
      setSelecting(false);
      // The person says what to change next.
      requestAnimationFrame(() =>
        document.querySelector<HTMLTextAreaElement>('.ws-chat-slot .aui-composer-input')?.focus(),
      );
    },
  };
}

export const AppCanvasTools: FC<{
  selecting: boolean;
  onSelecting: (on: boolean) => void;
  canSelect: boolean;
  updating: boolean;
  /** What Select points at, when it is not an App (the storefront canvas). */
  hint?: string;
}> = ({ selecting, onSelecting, canSelect, updating, hint }) => {
  const copy = useNamespaceCopy(buildWatchCopy);
  return (
    <>
      {canSelect && (
        <CanvasHeaderLeadTools>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="ap-select-toggle"
            aria-pressed={selecting}
            aria-label={copy.selectToggle}
            title={hint ?? copy.selectHint}
            onClick={() => onSelecting(!selecting)}
            onKeyDown={(event) => {
              // Esc leaves Select rather than closing the canvas.
              if (event.key === 'Escape' && selecting) {
                event.preventDefault();
                onSelecting(false);
              }
            }}
          >
            <WorkspaceIcon name="pointer" />
            <span className="ap-select-label">{copy.selectToggle}</span>
          </Button>
        </CanvasHeaderLeadTools>
      )}
      {updating && (
        <CanvasHeaderTools>
          <span className="ap-updating" role="status">
            <span className="ap-updating-dot" aria-hidden="true" />
            <span className="ap-updating-label">{copy.cardUpdating}</span>
          </span>
        </CanvasHeaderTools>
      )}
    </>
  );
};

export const SelectHint: FC<{ hint?: string }> = ({ hint }) => {
  const copy = useNamespaceCopy(buildWatchCopy);
  return (
    <p className="ap-select-hint" role="status">
      {hint ?? copy.selectHint}
    </p>
  );
};
